import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Edit as Edit2Icon, Delete as Trash2Icon, Add as PlusIcon, InfoOutlined as AlertCircleIcon } from '@mui/icons-material';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';

// ============================================================================
// TYPESCRIPT INTERFACES
// ============================================================================

interface Step {
  uid: string;
  _id?: string;
  title: string;
}

interface IntegrationConfig {
  _id: string;
  teamUid: string;
  stepUid: string[];
  integrationType: 'webhook' | 'smartsheet';
  configFields: any[];
  createdAt: Date;
  updatedAt: Date;
}

interface IntegrationListProps {
  configurations: IntegrationConfig[];
  stepOptions: Step[];
  isLoading: boolean;
  onEdit: (config: IntegrationConfig) => void;
  onDelete: (configId: string) => void;
  onAddNew: () => void;
  theme: any;
}

// ============================================================================
// INTEGRATION LIST COMPONENT
// ============================================================================

const IntegrationList: React.FC<IntegrationListProps> = ({
  configurations,
  stepOptions,
  isLoading,
  onEdit,
  onDelete,
  onAddNew,
  theme
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const hasIntegrationPermission = useHasPermission(PERMISSIONS.ADD_EDIT_INTEGRATION_CONFIG);

  const getStepNames = (stepIds: string[]): any => {
    if(stepIds.length>0&&stepOptions.length>0){
    return stepIds
      .map(id => stepOptions.find(s => s.uid === id || s._id === id)?.title)
      .join(', ');
      }
  };


  if (isLoading) {
    return (
      <Box sx={{ 
        p: 8, 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress size={48} thickness={4} sx={{ color: 'secondary.dark', mb: 3 }} />
        <Typography sx={{ color: 'text.secondary', fontSize: '0.9375rem', fontWeight: 500 }}>
          Loading integrations...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            Integrations
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Manage your integration configurations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PlusIcon sx={{ fontSize: 20 }} />}
          onClick={onAddNew}
          disabled={!hasIntegrationPermission}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '12px',
            px: 1,
            py: 0.8,
            backgroundColor: 'secondary.dark',
            boxShadow: 3,
            '&:hover': {
              backgroundColor: 'secondary.dark',
              boxShadow: 4,
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s',
          }}
        >
          Add New
        </Button>
      </Box>

      {configurations.length === 0 ? (
        <Card
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            backgroundColor: 'background.default',
            textAlign: 'center',
            py: 8,
            px: 3,
            cursor: 'pointer',
            transition: 'all 0.3s',
            borderRadius: '16px',
            '&:hover': {
              borderColor: 'secondary.light',
              backgroundColor: 'action.hover',
              boxShadow: 1,
            }
          }}
          onClick={onAddNew}
        >
          <Box sx={{ 
            width: 64, 
            height: 64, 
            borderRadius: '16px', 
            backgroundColor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <AlertCircleIcon sx={{ fontSize: 32, color: 'secondary.dark' }} />
          </Box>
          <Typography variant="h6" sx={{ color: 'text.primary', mb: 1, fontWeight: 600 }}>
            No integrations yet
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', maxWidth: 400, margin: '0 auto' }}>
            Get started by creating your first integration configuration
          </Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {configurations.map((config) => {
            return (
              <Card
                key={config._id}
                sx={{
                  borderRadius: '16px',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 1,
                  // transition: 'all 0.3s',
                  // '&:hover': {
                  //   boxShadow: 4,
                  //    transform: 'translateY(-4px)',
                  // }
                }}
              >
                <CardContent sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1.5 }}>
                    <Chip
                      label={config.integrationType.charAt(0).toUpperCase() + config.integrationType.slice(1)}
                      sx={{
                        backgroundColor: 'secondary.light',
                        color: 'secondary.dark',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        height: 28,
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(config)}
                        disabled={!hasIntegrationPermission}
                        sx={{ 
                          color: 'text.secondary',
                          backgroundColor: 'action.hover',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                            color: 'secondary.dark',
                          }
                        }}
                      >
                        <Edit2Icon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirm(config._id)}
                        disabled={!hasIntegrationPermission}
                        sx={{ 
                          color: 'text.secondary',
                          backgroundColor: 'action.hover',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.main',
                          }
                        }}
                      >
                        <Trash2Icon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                      STEPS
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontSize: '0.9375rem', mt: 0.5, fontWeight: 500, lineHeight: 1.6 }}>
                      {getStepNames(config.stepUid)}
                    </Typography>
                  </Box>

                  {config.configFields && config.configFields.length > 0 && (
                    <Box sx={{ 
                      backgroundColor: 'action.hover', 
                      borderRadius: '12px', 
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.5px', mb: 0.5, display: 'block' }}>
                        CONFIGURATION
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {config.configFields.map((field, idx) => (
                          <Typography key={idx} sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.5 }}>
                            {Object.keys(field).map(k => `${k}: ${field[k]}`).join(', ')}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {deleteConfirm === config._id && (
                    <Dialog 
                      open={true} 
                      onClose={() => setDeleteConfirm(null)}
                      PaperProps={{
                        sx: {
                          borderRadius: '16px',
                          minWidth: 400,
                        }
                      }}
                    >
                      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', color: 'text.primary', pb: 1 }}>
                        Delete Integration
                      </DialogTitle>
                      <DialogContent sx={{ pb: 2 }}>
                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          Are you sure you want to delete this integration configuration? This action cannot be undone.
                        </Typography>
                      </DialogContent>
                      <DialogActions sx={{ p: 3, pt: 2 }}>
                        <Button 
                          onClick={() => setDeleteConfirm(null)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: 'text.secondary',
                            borderRadius: '10px',
                            px: 3,
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            }
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => {
                            onDelete(config._id);
                            setDeleteConfirm(null);
                          }}
                          variant="contained"
                          color="error"
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '10px',
                            px: 3,
                            boxShadow: 2,
                            '&:hover': {
                              boxShadow: 3,
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </DialogActions>
                    </Dialog>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default IntegrationList;